/**
 * Os cabeçalhos que o app põe em todo pedido à API (a plataforma, o fuso e o código do
 * aparelho), num módulo sem dependência do login: o `apiFetch` usa (`lib/api.ts`), os
 * serviços que ainda montam o `fetch` na mão juntam aos deles, e o `auth-service`, que
 * não pode importar o `lib/api.ts` (o `lib/api.ts` importa ele), importa daqui.
 */
import { detectPlataformaOrigem } from "./utils/detect-platform"
import { cabecalhoDoAparelho } from "./codigo-do-aparelho"
import { versaoDaCasca } from "./versao-do-app"

/**
 * Fuso IANA do aparelho (`America/Sao_Paulo`, `Europe/Lisbon`). Null quando o
 * navegador não sabe informar, e aí o servidor cai no fuso salvo do usuário e,
 * por último, em BRT.
 */
export function detectarFusoIana(): string | null {
  if (typeof window === "undefined") return null
  try {
    const fuso = Intl.DateTimeFormat().resolvedOptions().timeZone
    // Um IANA sempre tem "Região/Cidade" ou é "UTC". Qualquer outra coisa (um
    // offset fixo tipo "GMT-3") é descartada: offset fixo não sabe de horário de
    // verão, que é justamente o que o IANA existe para resolver.
    return fuso && (fuso.includes("/") || fuso === "UTC") ? fuso : null
  } catch {
    return null
  }
}

function getNativePlatformHeader(): string | null {
  if (typeof window === "undefined") return null
  const p = (window as any).__WELLTRAINER_NATIVE_PLATFORM__
  if (p === "ios" || p === "android") return p
  return null
}

/**
 * Os cabeçalhos que o app põe em TODO pedido à API, menos o login e o Content-Type: a
 * plataforma, o fuso e o código do aparelho. O `apiFetch` usa, e o serviço que ainda
 * monta o `fetch` na mão junta este aos dele: sem eles, a API não sabe de que aparelho
 * nem de que plataforma o pedido vem (BUG-0026 do QA, 30/10/2026: a agenda e o aviso de
 * mensagem da casa do personal saíam sem nenhum).
 */
export function cabecalhosDoApp(): Record<string, string> {
  const headers: Record<string, string> = {}
  const nativePlatform = getNativePlatformHeader()
  if (nativePlatform) {
    headers["X-Native-Platform"] = nativePlatform
  }
  // A versão do app de loja (a casca), só dentro dela: a API guarda quando muda,
  // e é assim que se sabe quem está com o app velho. Ver `lib/versao-do-app.ts`.
  const casca = versaoDaCasca()
  if (casca) {
    headers["X-App-Versao"] = casca.rotulo
  }
  // Plataforma de origem normalizada (app/mobile/desktop) para a Jornada.
  headers["X-Plataforma-Origem"] = detectPlataformaOrigem()
  // Fuso IANA do aparelho, em TODA requisição. É o que permite ao servidor saber
  // que dia é "hoje" para ESTE usuário (treino, ofensiva, lembrete) sem depender
  // do relógio dele, que pode estar errado: o servidor usa o próprio relógio e o
  // fuso que vem daqui. Antes isso só chegava quando o app nativo registrava
  // push token, uma vez, então quem usava pelo navegador nunca mandava e quem
  // viajava ficava com o fuso antigo. Ver `ai/datas-e-fusos.md`, onda 3.
  const fuso = detectarFusoIana()
  if (fuso) {
    headers["X-Timezone"] = fuso
  }
  // O código do aparelho, sorteado e guardado neste navegador: é o que deixa a API
  // barrar o aparelho de quem foi banido. Ver `lib/codigo-do-aparelho.ts`.
  Object.assign(headers, cabecalhoDoAparelho())
  return headers
}
